import { useNavigate, useSearchParams } from "@solidjs/router";
import { createForm } from "@tanstack/solid-form";
import { Show } from "solid-js";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { CompanyLayoutMainContentContainer } from "@/components/layout/CompanyLayout";
import { Container } from "@/components/shared/Container";
import { TagInput } from "@/components/shared/input";
import { Button } from "@/components/ui/button";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  Input,
} from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MetaTitleWithSeparator } from "@/domains/business/seo";
import { SmartFilters } from "@/domains/company/active-search/components/SmartFilters";
import { CategorySubcategoryField } from "@/domains/company/active-search/components/subcategory-filter/CategorySubcategoryField";
import {
  buildPathWithQueryParams,
  freelancerFilterFormToQueryParams,
  searchParamsToFormDefaultValues,
} from "@/domains/company/active-search/utils/filterParams";
import { clearResultReachedViaRerun } from "@/domains/company/recent-searches/store/rerun-intent";
import {
  EXPERIENCE_LEVEL,
  JobCategoryEnum,
  type RemotePolicy,
} from "@/domains/job-offer/create/constants";
import {
  MAX_SKILL_TERM_LENGTH,
  MAX_SKILL_TERMS,
  optionalSemanticSearchSchema,
  optionalSkillTermsSchema,
} from "@/libs/skill-input";

const WORK_PREFERENCE_OPTIONS = ["remote", "onsite", "hybrid"] as const;
const PRICE_MIN = 0;
const PRICE_MAX = 1000;
const PRICE_STEP = 25;

const searchFormSchema = z.object({
  search: optionalSemanticSearchSchema,
  skills: optionalSkillTermsSchema,
  job_category: z
    .enum(Object.values(JobCategoryEnum) as [string, ...string[]])
    .optional(),
  job_subcategory: z.array(z.string()).optional(),
  work_preference: z.string().optional(),
  price_per_day_min: z.string().optional(),
  price_per_day_max: z.string().optional(),
  experience_level: z.enum(EXPERIENCE_LEVEL).optional(),
});

type SearchFormValues = z.infer<typeof searchFormSchema>;

export default function CompanySearchFreelancerSearchRoute() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getInitialValues = (): SearchFormValues => {
    const defaults = searchParamsToFormDefaultValues(searchParams);
    const job_category = Object.values(JobCategoryEnum).includes(
      defaults.job_category as JobCategoryEnum,
    )
      ? (defaults.job_category as JobCategoryEnum)
      : undefined;

    return {
      search: defaults.search,
      skills: defaults.skills,
      job_category,
      job_subcategory: job_category ? defaults.job_subcategory : [],
      work_preference: defaults.work_preference || undefined,
      price_per_day_min: defaults.price_per_day_min || undefined,
      price_per_day_max: defaults.price_per_day_max || undefined,
    };
  };

  const form = createForm(() => ({
    defaultValues: getInitialValues() as SearchFormValues,
    onSubmit: async ({ value }) => {
      const validatedData = searchFormSchema.safeParse(value);
      if (!validatedData.success) {
        console.error("Validation error", validatedData.error);
        return;
      }

      const params = freelancerFilterFormToQueryParams({
        ...validatedData.data,
        job_offer_id:
          typeof searchParams.job_offer_id === "string"
            ? searchParams.job_offer_id
            : undefined,
      });

      // A normal from-scratch entry into `/result`, not a rerun: an earlier
      // rerun's flag must not leak into this fresh navigation (see rerun-intent.ts).
      clearResultReachedViaRerun();
      navigate(
        buildPathWithQueryParams(
          "/profile/company/search/freelancers/result",
          params,
        ),
      );
    },
  }));

  return (
    <>
      <MetaTitleWithSeparator>
        {t("company.search.freelancers.search.title")}
      </MetaTitleWithSeparator>

      <CompanyLayoutMainContentContainer>
        <Container size="6xl" class="mb-6 mx-auto">
          {/* Page header */}
          <div class="mb-6">
            <h1 class="font-display text-xl font-semibold text-foreground mb-1">
              {t("company.search.freelancers.search.title")}
            </h1>
            <p class="text-sm text-muted-foreground">
              {t("company.search.freelancers.search.subtitle")}
            </p>
          </div>

          {/* Main card */}
          <div class="rounded-xl border border-border bg-card p-6">
            {/* Smart Filters section */}
            <SmartFilters
              onFiltersExtracted={(values) => {
                if (values.search !== undefined)
                  form.setFieldValue("search", values.search);
                if (values.skills !== undefined)
                  form.setFieldValue("skills", values.skills);
                if (values.job_category !== undefined) {
                  form.setFieldValue("job_category", values.job_category);
                  form.setFieldValue(
                    "job_subcategory",
                    values.job_subcategory ?? [],
                  );
                }
                if (values.work_preference !== undefined)
                  form.setFieldValue("work_preference", values.work_preference);
                if (values.price_per_day_min !== undefined)
                  form.setFieldValue(
                    "price_per_day_min",
                    values.price_per_day_min,
                  );
                if (values.price_per_day_max !== undefined)
                  form.setFieldValue(
                    "price_per_day_max",
                    values.price_per_day_max,
                  );
                if (values.experience_level !== undefined)
                  form.setFieldValue(
                    "experience_level",
                    values.experience_level as SearchFormValues["experience_level"],
                  );
              }}
            />

            {/* Divider */}
            <div class="relative my-6">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-border" />
              </div>
              <div class="relative flex justify-center">
                <span class="bg-card px-4 text-xs font-medium tracking-widest text-muted-foreground uppercase">
                  {String(
                    t("company.search.freelancers.search.manual_divider"),
                  )}
                </span>
              </div>
            </div>

            {/* Manual form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
              class="flex flex-col gap-4"
            >
              {/* Title */}
              <form.Field
                name="search"
                validators={{
                  onChange: searchFormSchema.shape.search,
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for={field().name}>
                      {t("company.search.freelancers.search.form.title.label")}
                    </FieldContainerLabel>
                    <Input
                      id={field().name}
                      name={field().name}
                      type="text"
                      value={field().state.value ?? ""}
                      onInput={(e) => field().handleChange(e.target.value)}
                      onBlur={() => field().handleBlur()}
                      onChange={(e) => field().handleChange(e.target.value)}
                      placeholder={t(
                        "company.search.freelancers.search.form.title.placeholder",
                      )}
                    />
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>

              {/* Skills */}
              <form.Field
                name="skills"
                validators={{
                  onChange: searchFormSchema.shape.skills,
                }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for={field().name}>
                      {t("company.search.freelancers.search.form.skills.label")}
                    </FieldContainerLabel>
                    <TagInput
                      fieldApi={field()}
                      class="bg-white"
                      maxTags={MAX_SKILL_TERMS}
                      maxTagLength={MAX_SKILL_TERM_LENGTH}
                    />
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>

              {/* Category + Work preference — two columns */}
              <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <form.Field
                  name="job_category"
                  validators={{
                    onChange: searchFormSchema.shape.job_category,
                  }}
                >
                  {(categoryField) => (
                    <form.Field name="job_subcategory" mode="array">
                      {(subcategoryField) => (
                        <FieldContainer>
                          <CategorySubcategoryField
                            surface="search"
                            category={categoryField().state.value ?? ""}
                            subcategories={subcategoryField().state.value ?? []}
                            onChange={(v) => {
                              form.setFieldValue(
                                "job_category",
                                (v.category ||
                                  undefined) as SearchFormValues["job_category"],
                              );
                              form.setFieldValue(
                                "job_subcategory",
                                v.subcategories,
                              );
                            }}
                          />
                          <FieldInfo field={categoryField()} />
                        </FieldContainer>
                      )}
                    </form.Field>
                  )}
                </form.Field>

                <form.Field
                  name="work_preference"
                  validators={{
                    onChange: searchFormSchema.shape.work_preference,
                  }}
                >
                  {(field) => (
                    <FieldContainer>
                      <FieldContainerLabel for={field().name}>
                        {t(
                          "company.search.freelancers.result.filters.work_preference.label",
                        )}
                      </FieldContainerLabel>
                      <Select
                        value={field().state.value ?? undefined}
                        onChange={(value) => field().handleChange(value ?? "")}
                        options={[...WORK_PREFERENCE_OPTIONS]}
                        placeholder={t(
                          "company.search.freelancers.result.filters.work_preference.placeholder",
                        )}
                        itemComponent={(item) => (
                          <SelectItem item={item.item}>
                            <span>
                              {t(
                                `common.remote_policy_options.${item.item.rawValue as RemotePolicy}`,
                              )}
                            </span>
                          </SelectItem>
                        )}
                      >
                        <SelectTrigger class="bg-white">
                          <SelectValue>
                            <Show
                              when={field().state.value}
                              fallback={
                                <span>
                                  {t(
                                    "company.search.freelancers.result.filters.work_preference.placeholder",
                                  )}
                                </span>
                              }
                            >
                              {(val) => (
                                <span>
                                  {val()
                                    ? String(
                                        t(
                                          `common.remote_policy_options.${val() as RemotePolicy}`,
                                        ),
                                      )
                                    : t(
                                        "company.search.freelancers.result.filters.work_preference.placeholder",
                                      )}
                                </span>
                              )}
                            </Show>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent />
                      </Select>
                      <FieldInfo field={field()} />
                    </FieldContainer>
                  )}
                </form.Field>
              </div>

              {/* Price range */}
              <form.Field
                name="price_per_day_min"
                validators={{
                  onChange: searchFormSchema.shape.price_per_day_min,
                }}
              >
                {(fieldMin) => (
                  <form.Field
                    name="price_per_day_max"
                    validators={{
                      onChange: searchFormSchema.shape.price_per_day_max,
                    }}
                  >
                    {(fieldMax) => {
                      const minVal = () => {
                        const v = fieldMin().state.value;
                        const n = v ? Number.parseInt(v, 10) : PRICE_MIN;
                        return Number.isNaN(n)
                          ? PRICE_MIN
                          : Math.min(Math.max(n, PRICE_MIN), PRICE_MAX);
                      };
                      const maxVal = () => {
                        const v = fieldMax().state.value;
                        const n = v ? Number.parseInt(v, 10) : PRICE_MAX;
                        return Number.isNaN(n)
                          ? PRICE_MAX
                          : Math.min(Math.max(n, PRICE_MIN), PRICE_MAX);
                      };
                      const safeMin = () => Math.min(minVal(), maxVal());
                      const safeMax = () => Math.max(minVal(), maxVal());
                      return (
                        <FieldContainer>
                          <FieldContainerLabel>
                            {t(
                              "company.search.freelancers.result.filters.price_range.label",
                            )}
                          </FieldContainerLabel>
                          <div class="space-y-3 pt-1">
                            <div class="flex items-center justify-between gap-2 text-sm text-muted-foreground">
                              <span>{safeMin()} €/g</span>
                              <span>{safeMax()} €/g</span>
                            </div>
                            <div class="flex gap-2 items-center">
                              <input
                                type="range"
                                min={PRICE_MIN}
                                max={PRICE_MAX}
                                step={PRICE_STEP}
                                value={safeMin()}
                                id={fieldMin().name}
                                name={fieldMin().name}
                                onInput={(e) => {
                                  const v = Number(e.currentTarget.value);
                                  fieldMin().handleChange(String(v));
                                  if (v > maxVal())
                                    fieldMax().handleChange(String(v));
                                }}
                                class="flex-1 h-2 rounded-lg appearance-none cursor-pointer bg-neutral-200 accent-primary"
                                aria-label={t(
                                  "company.search.freelancers.result.filters.price_per_day_min.label",
                                )}
                              />
                              <input
                                type="range"
                                min={PRICE_MIN}
                                max={PRICE_MAX}
                                step={PRICE_STEP}
                                value={safeMax()}
                                id={fieldMax().name}
                                name={fieldMax().name}
                                onInput={(e) => {
                                  const v = Number(e.currentTarget.value);
                                  fieldMax().handleChange(String(v));
                                  if (v < minVal())
                                    fieldMin().handleChange(String(v));
                                }}
                                class="flex-1 h-2 rounded-lg appearance-none cursor-pointer bg-neutral-200 accent-primary"
                                aria-label={t(
                                  "company.search.freelancers.result.filters.price_per_day_max.label",
                                )}
                              />
                            </div>
                          </div>
                        </FieldContainer>
                      );
                    }}
                  </form.Field>
                )}
              </form.Field>

              {/* Submit */}
              <form.Subscribe
                selector={(state) => ({
                  canSubmit: state.canSubmit,
                  isSubmitting: state.isSubmitting,
                })}
              >
                {(state) => (
                  <Button
                    type="submit"
                    class="w-full mt-2"
                    disabled={state().isSubmitting}
                  >
                    <i class="ph ph-magnifying-glass text-lg" />
                    {t("company.search.freelancers.search.form.submit")}
                  </Button>
                )}
              </form.Subscribe>
            </form>
          </div>
        </Container>
      </CompanyLayoutMainContentContainer>
    </>
  );
}
